/**
 * Etiqueta d'un lot — el contingut, el QR i el ZPL, PURS (Existències · OBR-178, reimpressió d'etiquetes).
 *
 * UN SOL GENERADOR. L'etiqueta d'un lot —la que planta imprimeix en rebre'l (OBR-85) i la que el BackOffice o l'App reimpriuen
 * des d'Existències— ha de dir EXACTAMENT el mateix lot, la mateixa caducitat i dur el mateix QR, sigui quina sigui la
 * superfície que la fa. Per això el contingut es compon aquí ({@link buildLotLabelContent}) i cada superfície només el dibuixa:
 *   · planta, en ZPL (`renderInternalLotLabelZpl`, mogut aquí des de `apps/planta/src/lib/labelZpl.ts` tal com era, byte a byte:
 *     `labelZpl.ts` el reexporta);
 *   · el BackOffice, en PDF (`features/stock/labels-reprint`), amb els mateixos camps.
 *
 * INVARIANT (CLAUDE.md): el número de lot és d'autoritat del servidor. Qui imprimeix només passa números CANÒNICS (mai el
 * provisional de l'aparell); aquest renderitzador es nega a imprimir un número de lot o un QR buits.
 *
 * Aquest fitxer també diu què és una REIMPRESSIÓ (motius, còpies, errors de l'RPC `record_lot_label_reprint`) perquè el
 * BackOffice i l'App ho comparteixin; el registre mateix és a la base de dades (`lot_label_reprints`).
 *
 * PURESA: composició de text; cap E/S, cap rellotge, cap atzar, cap `Date` (les dates d'etiqueta són dates de calendari).
 */

import { lotTraceUrl } from './label.js';

// ── La reimpressió: motius, origen, còpies ───────────────────────────────────────────────────

/** Per què es reimprimeix (decisió D8): no es va etiquetar · l'etiqueta s'ha fet malbé · altres (demana una nota). */
export const LOT_LABEL_REPRINT_REASONS = ['not_labelled', 'damaged', 'other'] as const;
export type LotLabelReprintReason = (typeof LOT_LABEL_REPRINT_REASONS)[number];

/** Des d'on s'ha fet: el BackOffice (PDF) o l'App de planta (Zebra). */
export const LOT_LABEL_REPRINT_ORIGINS = ['backoffice', 'app'] as const;
export type LotLabelReprintOrigin = (typeof LOT_LABEL_REPRINT_ORIGINS)[number];

/** La nota lliure d'una reimpressió: com a molt tants caràcters (la base de dades també els limita: CHECK i RPC; una nota és un registre immutable). */
export const LOT_LABEL_REPRINT_NOTE_MAX = 500;

/** Quantes còpies com a mínim d'una impressió. */
export const MIN_LABEL_COPIES = 1;

/** The most copies one print job asks for: a pallet's worth of labels, never a typo's worth. */
export const MAX_LABEL_COPIES = 99;

/**
 * A number of copies as the stepper hands it over, brought into what the printer accepts: a whole
 * number from 1 to {@link MAX_LABEL_COPIES}. A value that is not a number at all becomes 1 — the
 * stepper never prints «nothing» or «NaN labels».
 */
export function clampCopies(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(MAX_LABEL_COPIES, Math.max(MIN_LABEL_COPIES, Math.round(value)));
}

/** Un nombre de còpies vàlid per a l'RPC: enter de 1 a 99. */
export function isValidLabelCopies(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= MIN_LABEL_COPIES &&
    value <= MAX_LABEL_COPIES
  );
}

/** Els errors de `record_lot_label_reprint` i `lot_label_reprint_history` (el codi va al `message`). */
export const LOT_LABEL_REPRINT_ERROR_CODES = [
  'lot_label_reprint_forbidden',
  'lot_label_reprint_bad_copies',
  'lot_label_reprint_bad_reason',
  'lot_label_reprint_note_required',
  'lot_label_reprint_note_too_long',
  'lot_label_reprint_bad_printed_at',
  'lot_label_reprint_bad_expiry',
  'lot_label_reprint_bad_origin',
  'lot_label_reprint_lot_required',
  'lot_label_reprint_unknown_lot',
  'lot_label_reprint_bad_place',
  'lot_label_reprint_client_uuid_conflict',
] as const;
export type LotLabelReprintErrorCode = (typeof LOT_LABEL_REPRINT_ERROR_CODES)[number];

/** El codi d'aplicació que porta un missatge d'error (l'SDK hi pot afegir context), o null si no n'hi ha cap de conegut. */
export function lotLabelReprintErrorCode(
  message: string | null | undefined,
): LotLabelReprintErrorCode | null {
  if (!message) return null;
  return LOT_LABEL_REPRINT_ERROR_CODES.find((code) => message.includes(code)) ?? null;
}

/**
 * La clau i18n de la frase d'obrador de cada error (`backoffice.lotLabelReprint`). Cap error no es mostra mai cru: un codi que
 * no es coneix surt amb la frase genèrica {@link LOT_LABEL_REPRINT_ERROR_GENERIC_KEY}.
 */
export const LOT_LABEL_REPRINT_ERROR_I18N_KEYS: Readonly<Record<LotLabelReprintErrorCode, string>> = {
  lot_label_reprint_forbidden: 'backoffice.lotLabelReprint:errForbidden',
  lot_label_reprint_bad_copies: 'backoffice.lotLabelReprint:errBadCopies',
  lot_label_reprint_bad_reason: 'backoffice.lotLabelReprint:errBadReason',
  lot_label_reprint_note_required: 'backoffice.lotLabelReprint:errNoteRequired',
  lot_label_reprint_note_too_long: 'backoffice.lotLabelReprint:errNoteTooLong',
  lot_label_reprint_bad_printed_at: 'backoffice.lotLabelReprint:errGeneric',
  lot_label_reprint_bad_expiry: 'backoffice.lotLabelReprint:errGeneric',
  lot_label_reprint_bad_origin: 'backoffice.lotLabelReprint:errGeneric',
  lot_label_reprint_lot_required: 'backoffice.lotLabelReprint:errLotRequired',
  lot_label_reprint_unknown_lot: 'backoffice.lotLabelReprint:errUnknownLot',
  lot_label_reprint_bad_place: 'backoffice.lotLabelReprint:errBadPlace',
  lot_label_reprint_client_uuid_conflict: 'backoffice.lotLabelReprint:errGeneric',
};

export const LOT_LABEL_REPRINT_ERROR_GENERIC_KEY = 'backoffice.lotLabelReprint:errGeneric';

/** La clau i18n del missatge d'un error de les RPC de reimpressió (la frase genèrica quan no se'l coneix). */
export function lotLabelReprintErrorKey(error: unknown): string {
  const message =
    typeof error === 'string' ? error : error instanceof Error ? error.message : undefined;
  const code = lotLabelReprintErrorCode(message);
  return code === null ? LOT_LABEL_REPRINT_ERROR_GENERIC_KEY : LOT_LABEL_REPRINT_ERROR_I18N_KEYS[code];
}

/** Què sap la pantalla d'una reimpressió abans de registrar-la. */
export interface LotLabelReprintFacts {
  /** Hi ha un lot (de proveïdor o de producció) sobre el qual etiquetar. */
  readonly hasLot: boolean;
  readonly copies: number;
  /** El motiu triat, o null mentre no n'hi ha cap. */
  readonly reason: string | null;
  /** La nota lliure. */
  readonly note: string | null;
}

/** Quin camp de la reimpressió falla. */
export type LotLabelReprintIssueField = 'lot' | 'copies' | 'reason' | 'note';

export type LotLabelReprintCheck =
  | { readonly ok: true }
  | { readonly ok: false; readonly field: LotLabelReprintIssueField; readonly code: LotLabelReprintErrorCode };

/**
 * Mirall PUR de les comprovacions de `record_lot_label_reprint` que la pantalla pot fer abans d'enviar res: hi ha lot, còpies d'1
 * a 99, motiu triat i, si és «altres», una nota escrita. El servidor ho torna a comprovar (aquesta és l'ajuda, no la guarda).
 */
export function checkLotLabelReprint(facts: LotLabelReprintFacts): LotLabelReprintCheck {
  if (!facts.hasLot) {
    return { ok: false, field: 'lot', code: 'lot_label_reprint_lot_required' };
  }
  if (!isValidLabelCopies(facts.copies)) {
    return { ok: false, field: 'copies', code: 'lot_label_reprint_bad_copies' };
  }
  const reason = LOT_LABEL_REPRINT_REASONS.find((r) => r === facts.reason);
  if (reason === undefined) {
    return { ok: false, field: 'reason', code: 'lot_label_reprint_bad_reason' };
  }
  if (reason === 'other' && (facts.note ?? '').trim() === '') {
    return { ok: false, field: 'note', code: 'lot_label_reprint_note_required' };
  }
  if ((facts.note ?? '').trim().length > LOT_LABEL_REPRINT_NOTE_MAX) {
    return { ok: false, field: 'note', code: 'lot_label_reprint_note_too_long' };
  }
  return { ok: true };
}

// ── El contingut de l'etiqueta ───────────────────────────────────────────────────────────────

/** El que diu l'etiqueta d'un lot, igual a totes les superfícies. */
export interface LotLabelContent {
  /** El número de lot CANÒNIC (p. ex. 'MP-L-00042'; el de producció, el de l'OT). */
  readonly lotNumber: string;
  readonly articleName: string;
  /** YYYY-MM-DD, o null quan el lot no en té. */
  readonly expiryDate: string | null;
  /** La càrrega del QR: l'URL pública de traçabilitat del lot (`lotTraceUrl`). */
  readonly qrPayload: string;
}

/**
 * Compon el contingut de l'etiqueta d'un lot: el número de lot tal com és, el nom de l'article, la caducitat i el QR derivat del
 * número de lot. És l'ÚNIC lloc que decideix què porta una etiqueta de lot.
 *
 * @throws Error quan el número de lot és buit (mai s'etiqueta un lot en blanc).
 */
export function buildLotLabelContent(input: {
  readonly lotNumber: string;
  readonly articleName: string;
  readonly expiryDate?: string | null;
}): LotLabelContent {
  return {
    lotNumber: input.lotNumber,
    articleName: input.articleName,
    expiryDate: input.expiryDate ?? null,
    // lotTraceUrl refusa un número buit.
    qrPayload: lotTraceUrl(input.lotNumber),
  };
}

/**
 * Una data de calendari (YYYY-MM-DD) tal com la porta una etiqueta de paper: tot numèric, dd/mm/aaaa. Es talla el text, no es
 * passa per `Date` (una data sense hora es llegiria a mitjanit UTC i, a una zona negativa, sortiria el dia anterior). Una cadena
 * que no és una data surt tal com és (mai s'inventa un dia); null/buida, null.
 */
export function formatLabelDate(iso: string | null | undefined): string | null {
  if (iso === null || iso === undefined || iso.trim() === '') return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso.trim());
  if (!match) return iso.trim();
  return `${match[3]}/${match[2]}/${match[1]}`;
}

/**
 * L'etiqueta d'una reimpressió deia una caducitat diferent de la que el lot tenia quan es va registrar? (Dues dates iguals, o les dues
 * buides, no difereixen.) Serveix per marcar-ho a l'historial: es veu, no es perd.
 */
export function lotLabelExpiryDiffers(
  labelExpiry: string | null | undefined,
  lotExpiry: string | null | undefined,
): boolean {
  const norm = (value: string | null | undefined): string | null =>
    value === null || value === undefined || value.trim() === '' ? null : value.trim().slice(0, 10);
  return norm(labelExpiry) !== norm(lotExpiry);
}

// ── ZPL (planta) ─────────────────────────────────────────────────────────────────────────────
// MOGUT tal com era des de `apps/planta/src/lib/labelZpl.ts` (OBR-85 i F7). El que ja es feia no pot canviar de resultat: els
// tests de paritat (`lotLabel.test.ts`) fixen la sortida byte a byte.

/** 203-dpi thermal printers are 8 dots/mm — the standard reception-label resolution. */
export const DOTS_PER_MM = 8;

export interface InternalLotLabelInput {
  /** CANONICAL internal MP lot number (e.g. 'MP-L-00042'). */
  readonly lotNumber: string;
  readonly articleName: string;
  /** QR payload (public traceability URL, from lotTraceUrl). */
  readonly qrPayload: string;
  /** YYYY-MM-DD, optional. */
  readonly expiryDate?: string | null;
  /** YYYY-MM-DD reception date, optional. */
  readonly receptionDate?: string | null;
  /** Label size in mm (defaults to a 50×30 mm reception label). */
  readonly widthMm?: number;
  readonly heightMm?: number;
  /**
   * How many copies the printer prints of this ONE label (a reprint, OBR-85): a whole number
   * from 1 to {@link MAX_LABEL_COPIES}; absent = 1. It is the printer's own print quantity
   * (`^PQ`), not the same job sent N times, so the copies come out together.
   */
  readonly copies?: number;
}

/** Escape the ZPL field-data terminator characters that would break a ^FD block. */
export function escapeZpl(value: string): string {
  // '^' and '~' are ZPL command prefixes; strip them so field text can't inject commands.
  return value.replace(/[\^~]/g, ' ');
}

export function mmToDots(mm: number): number {
  return Math.round(mm * DOTS_PER_MM);
}

/**
 * Render the internal-lot label to a ZPL string ready to send to a thermal printer.
 *
 * @throws Error when the lot number or QR payload is empty (never print a blank/uncanonical lot),
 *         or when `copies` is not a whole number between 1 and {@link MAX_LABEL_COPIES}.
 */
export function renderInternalLotLabelZpl(input: InternalLotLabelInput): string {
  if (!input.lotNumber || input.lotNumber.trim() === '') {
    throw new Error('renderInternalLotLabelZpl: lotNumber is required');
  }
  if (!input.qrPayload || input.qrPayload.trim() === '') {
    throw new Error('renderInternalLotLabelZpl: qrPayload is required');
  }

  const copies = input.copies ?? 1;
  if (!Number.isInteger(copies) || copies < 1 || copies > MAX_LABEL_COPIES) {
    throw new Error(
      `renderInternalLotLabelZpl: copies must be a whole number from 1 to ${MAX_LABEL_COPIES}`,
    );
  }

  const widthDots = mmToDots(input.widthMm ?? 50);
  const heightDots = mmToDots(input.heightMm ?? 30);
  const article = escapeZpl(input.articleName);
  const lot = escapeZpl(input.lotNumber);
  const qr = escapeZpl(input.qrPayload);

  const lines: string[] = [
    '^XA',
    `^PW${widthDots}`,
    `^LL${heightDots}`,
    '^CI28', // UTF-8 input encoding (accented article names).
    '^CF0,24',
    `^FO16,16^FB${widthDots - 180},2,0,L,0^FD${article}^FS`,
    '^CF0,28',
    `^FO16,72^FDLot: ${lot}^FS`,
  ];
  if (input.expiryDate) {
    lines.push(`^FO16,108^FDCad: ${escapeZpl(input.expiryDate)}^FS`);
  }
  if (input.receptionDate) {
    lines.push(`^FO16,144^FDRec: ${escapeZpl(input.receptionDate)}^FS`);
  }
  // Traceability QR, anchored to the right side.
  lines.push(`^FO${widthDots - 160},64^BQN,2,5^FDLA,${qr}^FS`);
  // One copy is the printer's default: the label stays exactly what it always was.
  if (copies > 1) lines.push(`^PQ${copies}`);
  lines.push('^XZ');

  return lines.join('\n');
}
